<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import Badge from 'primevue/badge';
import type { CaseModel } from '@/models';
import { Popup } from '@/components';
import { formattedDate, DATE_FORMAT_2 } from '@/utils';
import { BACKEND_URL } from '@/services/apiClient';
import { useStudyStore } from '@/stores/study';
import ProgressBar from 'primevue/progressbar';

const props = defineProps({
  formIdentifier: {
    type: String,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  caseModel: {
    type: Object as PropType<CaseModel>,
    required: true
  }
});

const studyStore = useStudyStore();
const showEntryList = ref(false);
const badgeRef = ref();
const entryListXvalue = ref('0px');
const entryListYvalue = ref('0px');

const onBadgeClick = () => {
  showEntryList.value = !showEntryList.value;

  const bounds = badgeRef.value?.getBoundingClientRect();
  if (bounds) {
    entryListXvalue.value = `${bounds.x}px`;
    entryListYvalue.value = `${bounds.y + bounds.height + 4}px`;
  }
};

const entries = computed(() => {
  const finalEntries = props.caseModel.entries![props.formIdentifier];
  if (finalEntries) {
    return finalEntries.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()); 
  }
  return [];
});

const completion = computed(() => {
  if (entries.value.length > 1) {
    let completedEntries = 0;
    for (const entry of entries.value) {
      if ((entry.completion?.totalRequiredFields ?? 0) === (entry.completion?.answeredRequiredFields ?? 0)) {
        completedEntries++;
      }
    }

    return {
      total: entries.value.length,
      completed: completedEntries
    };
  }
  if (props.caseModel.entries![props.formIdentifier] && props.caseModel.entries![props.formIdentifier].length == 1) {
    const entry = props.caseModel.entries![props.formIdentifier][0];
    return {
      total: entry.completion?.totalRequiredFields ?? 0,
      completed: entry.completion?.answeredRequiredFields ?? 0,
      unanswered: entry.completion?.unansweredFields
    };
  }
  return !studyStore.selectedStudy?.flags?.entryCompletionLogic ? {total: 0, completed: 0 } : {total: 100, completed: 0 };
});

const completionPercentage = computed(() => {
  if (entries.value.length > 1) {
    let completedEntries = 0;
    for (const entry of entries.value) {
      if ((entry.completion?.totalRequiredFields ?? 0) === (entry.completion?.answeredRequiredFields ?? 0)) {
        completedEntries++;
      }
    }

    return (completedEntries * 100) / entries.value.length;
  }
  return (completion.value.completed * 100) / completion.value.total;
});

const completedIcon = computed(() => {
  return !studyStore.selectedStudy?.flags?.entryCompletionLogic ||
  (!(completion.value.total == 100 && completion.value.completed == 0) && completion.value.total == completion.value.completed);
});

const completionTooltip = computed(() => {
  return (completion.value.total > 0 || completion.value.completed > 0) &&
    !(completion.value.total == 100 && completion.value.completed == 0) ?
    `${completion.value.completed}/${completion.value.total} completed
      ${completion.value.unanswered?.length ? `\nIncomplete: \n${completion.value.unanswered.join('\n')}` : ''}` :
    completion.value.total === 0 ?
      '' :
      studyStore.selectedStudy?.flags?.entryCompletionLogic &&
      entries.value.length < 2 ?
        "Entry isn't submitted yet" :
        '';
});

const enforcements = computed(() => {
  return props.caseModel.enforcements ? props.caseModel.enforcements[props.formIdentifier] : undefined;
});

const emits = defineEmits(['onCellClick']);

const onCellClick = (e: MouseEvent, entryIdentifier?: string) => {
  if (showEntrySubmittedIcon.value) {
    emits('onCellClick', props.formIdentifier, entries.value.length ? entries.value[0].identifier : undefined);
  } else if (!enforceOrder.value && !enforcements.value?.hidden) {
    emits('onCellClick', props.formIdentifier, entryIdentifier);
  }
};

const showPlusIcon = computed(() => {
  if (enforcements.value) {
    if (
      (enforcements.value.onlyOnce && entries.value.length == 1) ||
      enforcements.value.hidden ||
      enforceOrder.value
    ) {
      return false;
    }
  }
  return true;
});

const showEntrySubmittedIcon = computed(() => {
  if (enforcements.value && enforcements.value.onlyOnce) {
    const entry = entries.value.length && entries.value.length === 1 ? entries.value[0] : undefined;
    return entry !== undefined;
  }
  return false;
});

const enforceOrder = computed(() => {
  if (
    props.index > 1 &&
    props.caseModel.enforcements &&
    props.caseModel.enforcements[props.formIdentifier] &&
    props.caseModel.enforcements[props.formIdentifier].enforceOrder) {
    return true;
  }
  return false;
});

const showEntryItemFieldAnswerInTitle = computed(() => {
  const dataObjectKey = `${studyStore.selectedStudy?.flags?.customDataDictionary}_DATA`;
  //@ts-expect-error: dynamic data for PUMP
  return studyStore.selectedStudy?.flags[dataObjectKey] ? studyStore.selectedStudy?.flags[dataObjectKey][props.formIdentifier] : false;
});

const allEntiresCompleted = () => {
  if (!entries.value.length) {
    return false;
  }
  return entries.value.every(entry => {
    const totalRequired = entry.completion?.totalRequiredFields ?? 0;
    const answeredRequired = entry.completion?.answeredRequiredFields ?? 0;
    return totalRequired === answeredRequired;
  });
};

const badgeColor = computed(() => {
  return allEntiresCompleted() ? '#189988' : '#d96b18';
});

const badgeHoverColor = computed(() => {
  return allEntiresCompleted() ? '#0c6f62' : '#a64b0a';
});
</script>

<template>
   <div class="cell-root" v-if="caseModel.loadingData">
    <div class="cell-info">
    <div mode="indeterminate" class="progress"></div>
    </div>
  </div>
  <div class="cell-root"
    v-ripple
    @click="onCellClick"
    v-if="!caseModel.loadingData"
    v-tooltip="completionTooltip ">
    <div class="cell-info" :class="entries ? `${completion.total > completion.completed && !allEntiresCompleted() ? 'incomplete' : 'entries'}` : ''">
      <div @click.stop="onBadgeClick" ref="badgeRef">
        <Badge :value="entries && entries.length"
          v-if="entries && entries.length > 1"/>
      </div>
      <FontAwesomeIcon :icon="['far', entries![0].lockedAt ? 'file-lock': 'file']"
        v-if="entries && entries.length == 1 && !showEntrySubmittedIcon"
        @click="(e: PointerEvent) => onCellClick(e, entries![0].identifier)"
        class="file"/>
      <FontAwesomeIcon :icon="['fas', 'file-circle-check']"
        v-if="showEntrySubmittedIcon && completedIcon"
        class="file"/>
      <FontAwesomeIcon :icon="['fas', 'file']"
        v-if="showEntrySubmittedIcon && !completedIcon"
        class="file-partial"/>
      <FontAwesomeIcon :icon="['far', 'circle-plus']"
        class="circle-plus"
        v-if="showPlusIcon"/>
      <FontAwesomeIcon :icon="['fas', 'xmark']"
        class="hidden"
        v-if="enforcements?.hidden"/>
      <FontAwesomeIcon :icon="['fas', 'lock-keyhole']"
        class="lock"
        v-if="!enforcements?.hidden && enforceOrder"/>
    </div>
    <div class="horizontal-progress"
      :class="completion.total == completion.completed ? 'horizontal-completed' : ''"
      v-if="completion.total > 0 || completion.completed > 0">
      <div class="horizontal-wrapper">
        <ProgressBar :value="completionPercentage" class="progress-text">{{}}</ProgressBar>
      </div>
    </div>
  </div>

<Popup :visible="showEntryList"
  :x="entryListXvalue"
  :y="entryListYvalue"
  @outsidePopupClick="onBadgeClick">
  <template #popupContent>
    <div class="entry-list-popup-content">
      <div class="popup-title">List of entries</div>
      <div v-for="entry in entries ?? []"
        :key="entry.identifier"
        @click="(e: MouseEvent) => onCellClick(e, entry.identifier)"
        v-ripple>
        <div class="entry-item">
          <FontAwesomeIcon :icon="['far', `file`]" />
          <div class="entry-item-label">
            {{ entry.formTitle }}
            {{ showEntryItemFieldAnswerInTitle ? `(${entry.items ? entry.items[showEntryItemFieldAnswerInTitle] : ''})` : '' }},
          </div>
          <div class="entry-item-label">
            {{ formattedDate(entry.createdAt, DATE_FORMAT_2) }}
          </div>
          <img :src="`${BACKEND_URL}${entry.creatorAvatar}`"
            class="avatar"
            v-if="entry.creatorAvatar"/>
          <div class="entry-item-creator">{{ entry.creatorNames }}</div>
        </div>
        <div class="horizontal-progress progress-popup"
          :class="entry.completion?.totalRequiredFields == entry.completion?.answeredRequiredFields ? 'horizontal-completed' : ''"
          v-if="(entry.completion?.totalRequiredFields ?? 0) > 0 || (entry.completion?.answeredRequiredFields ?? 0) > 0">
          <div class="horizontal-wrapper">
            <ProgressBar :value="((entry.completion?.answeredRequiredFields ?? 0) * 100) / (entry.completion?.totalRequiredFields ?? 1)"
              class="progress-text">
              {{}}
            </ProgressBar>
          </div>
        </div>
      </div>
    </div>
  </template>
</Popup>
</template>

<style scoped lang="scss">
@use '../../../assets/variables.module' as *;
/* Fills the <td> so the cell background/hover covers the whole row height,
   however tall the row's other columns make it. */
.cell-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.cell-info {
  width: 136px;
  min-height: 52px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-left: 2px solid white;
  border-right: 2px solid white;

  .circle-plus {
    font-size: 16px;
    color: #b8c2cc;
    margin-left: 8px;
  }

  .hidden {
    font-size: 16px;
    color: hsl(4, 90%, 58%);
    margin-left: 8px;
  }

  .lock {
    font-size: 16px;
    color: #b8c2cc;
    margin-left: 8px;
  }

  .file {
    font-size: 16px;
    color: #189988;

    &:hover {
      color: #0c6f62;
    }
  }

  &:hover {
    background-color: hsl(248.5, 62%, 90%);
    cursor: pointer;

    .circle-plus {
      color: $primary-color;
    }

    .hidden {
      color: #b8c2cc;
    }

    .lock {
      color: black;
    }
  }
}
.entries {
  background-color: #dff7f4;

  &:hover {
    background-color: #b6e6e0;
    cursor: pointer;

    .circle-plus {
      color: $primary-color;
    }
  }
}
.incomplete {
  background-color: #f7e5df;

  &:hover {
    background-color: #e6c2b6;
    cursor: pointer;

    .circle-plus {
      color: $primary-color;
    }
  }

  .file-partial {
    font-size: 16px;
    color: #d96b18;

    &:hover {
      color: #a64b0a;
    }
  }
}
.horizontal-progress {
  height: 4px;
  margin: 0px 2px;

  :deep(.p-progressbar) {
    height: 4px;
    border-radius: 0px;
    width: 100%;
    background: #d2a99f;
  }

  :deep(.p-progressbar-value) {
    font-size: 10px;
    background: #d96b18;
  }
}
.horizontal-completed {

  :deep(.p-progressbar-value) {
    font-size: 10px;
    background: #189988;
  }
}
.progress-popup {
  padding: 0px 16px;
}
:deep(.p-badge) {
  border-radius: 20px;
  background-color: v-bind(badgeColor);

  &:hover {
    background-color: v-bind(badgeHoverColor);
  }
}
.entry-list-popup-content {
  max-height: 50vh;
  overflow: auto;
}
.popup-title {
  padding: 12px 20px;
  color: #828e99;
}
.entry-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 12px 20px;
  color: #272a2e;
  cursor: pointer;

  &:hover {
    background-color: #f5f7fa;
  }

  .entry-item-label {
    margin-left: 12px;
    line-height: 16px;
  }

  .entry-item-creator {
    margin-left: 8px;
  }

  .avatar {
    width: 24px;
    margin-left: 16px;
    border-radius: 50%;
  }
}
.progress {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #b6e6e0 0%,
    #e0f7f5 20%,
    #ffffff 50%,
    #e0f7f5 80%,
    #b6e6e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 3s infinite linear;
  background-color: #b6e6e0;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
